import { type Workspace } from '@/features/iam/workspaces/Workspace'
import { FirmDashboardJobTableViewModel } from '@/features/jobs/dashboard/FirmDashboardJobTableViewModel'
import { type Storage } from '@/infra/storage'
import type {
  GetCurrentMemberFn,
  GetCurrentUserFn,
  GetCurrentUserWorkspaceMembershipsFn,
  GetTeamMembersForWorkspaceMemberFn,
  ListUserTeamMembersFn,
} from '@/features/iam'
import type {
  AlertErrorHandlerFactory,
  AlertFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'
import { FirmDashboardWorkspaceSearchViewModel } from '@/features/jobs/dashboard/FirmDashboardWorkspaceSearchViewModel'
import { FirmDashboardQuickSelectFilterViewModel } from '@/features/jobs/dashboard/FirmDashboardQuickSelectFilterViewModel'
import { type Member } from '@/features/iam/members/Member'
import { FirmDashboardOverviewSidebarViewModel } from '@/features/jobs/dashboard/FirmDashboardOverviewSidebarViewModel'
import { FirmDashboardContractViewModel } from '@/features/jobs/dashboard/FirmDashboardContractViewModel'
import { type JobStatsStore } from '@/features/jobs/dashboard/job-stats/JobStatsStore'
import { type SearchableJobStore } from '@/features/jobs/list/SearchableJobStore'
import { type JobSearchViewModelFactory } from '@/features/jobs/list/JobSearchViewModel'
import { type JobTicketStore } from '@/features/jobs/tickets/JobTicketStore'
import { type FilterStorageFactory } from '@/infra/FiltersViewModel'
import { action, computed } from 'mobx'
import { filter, type Observable } from 'rxjs'
import { type ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { tapAction } from '@/utils/mobx-util'
import { DefaultLastWorkspaceStorage } from '@/features/iam/workspaces/LastWorkspaceStorage'

/**
 * View model for managing the firm dashboard page.
 */
export class FirmDashboardViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Storage for the last selected workspace.
   */
  private readonly _lastWorkspaceStorage

  /**
   * The member the workspace selection was last synced for.
   */
  private _syncedMember: Member | null = null

  /**
   * Selects the workspace to filter by when the current member changes.
   */
  private readonly _workspaceSelectionSync$

  /**
   * Firm dashboard job's table view model.
   */
  readonly jobTableVm

  /**
   * Firm dashboard workspace selector view model.
   */
  readonly workspaceSelectorVm

  /**
   * Firm dashboard quick select filters view model.
   */
  readonly quickSelectFilterVm

  /**
   * Firm dashboard overview sidebar view model.
   */
  readonly overviewSidebarVm

  constructor(
    searchableJobStore: SearchableJobStore,
    jobStatsStore: JobStatsStore,
    jobTicketStore: JobTicketStore,
    createJobSearchViewModel: JobSearchViewModelFactory,
    createFilterStorage: FilterStorageFactory,
    newAlert: AlertFactory,
    createAlertErrorHandler: AlertErrorHandlerFactory,
    createAlertRetryWrapper: AlertRetryWrapperFactory,
    private readonly _getCurrentMember: GetCurrentMemberFn,
    getCurrentUser: GetCurrentUserFn,
    private readonly _getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMembershipsFn,
    getTeamMembersForWorkspaceMember: GetTeamMembersForWorkspaceMemberFn,
    listUserTeamMembers: ListUserTeamMembersFn,
    canEditDraftJobAsAdmin: () => boolean,
    currentMember$: Observable<Member>,
    localStorage: Storage,
  ) {
    this._lastWorkspaceStorage = new DefaultLastWorkspaceStorage(
      localStorage,
      'firmDashboard',
    )

    this.workspaceSelectorVm = new FirmDashboardWorkspaceSearchViewModel(
      _getCurrentUserWorkspaceMemberships,
      getTeamMembersForWorkspaceMember,
    )
    const getWorkspace = () => this.workspaceSelectorVm.workspace
    const contractVm = new FirmDashboardContractViewModel(getWorkspace)

    this.jobTableVm = new FirmDashboardJobTableViewModel(
      searchableJobStore,
      newAlert,
      createAlertErrorHandler,
      createJobSearchViewModel,
      listUserTeamMembers,
      jobTicketStore,
      createFilterStorage,
      canEditDraftJobAsAdmin,
      getWorkspace,
      contractVm,
    )
    this.quickSelectFilterVm = new FirmDashboardQuickSelectFilterViewModel(
      searchableJobStore,
      createAlertRetryWrapper,
      getWorkspace,
      contractVm,
      () => this.jobTableVm.statusFilter,
    )
    this.overviewSidebarVm = new FirmDashboardOverviewSidebarViewModel(
      searchableJobStore,
      createJobSearchViewModel,
      _getCurrentMember,
      createFilterStorage,
      jobStatsStore,
      getCurrentUser,
      getWorkspace,
      contractVm,
    )

    this._workspaceSelectionSync$ = currentMember$.pipe(
      // Every activation replays the current member; only a new member resets
      // the selection.
      filter((member) => member !== this._syncedMember),
      tapAction((member) => {
        this._syncedMember = member
        this._syncWorkspaceSelection(member)
      }),
    )

    this._lifecycle
      .manage(this._workspaceSelectionSync$)
      .manage(this.workspaceSelectorVm)
      .manage(this.quickSelectFilterVm)
      .manage(this.overviewSidebarVm)
      .manage(this.jobTableVm)
  }

  /**
   * All Workspaces where the user have the permission to view the dashboard.
   */
  @computed
  get filteredWorkspacesWhereUserCanViewDashboard() {
    return this.workspaceSelectorVm.filteredWorkspaces
  }

  /**
   * Whether to display the dropdown arrow
   * when user can view dashboard in more than 1 workspace.
   */
  @computed
  get workspaceSelectorUseDropdown() {
    return (
      this.workspaceSelectorVm.workspacesWhereUserCanViewDashboard.length > 1
    )
  }

  /**
   * Whether the current user can view the firm dashboard in any workspace.
   */
  @computed
  get canViewFirmDashboard() {
    return this._getCurrentUserWorkspaceMemberships().some(
      (wms) => wms.member.permissions.viewFirmDashboard,
    )
  }

  /**
   * Selects the workspace to filter by in the firm dashboard.
   */
  @action.bound
  selectWorkspaceForFiltering(workspace: Workspace) {
    this.workspaceSelectorVm.setWorkspace(workspace)

    const userId = this._getCurrentMember().user.publicId
    if (workspace.id && userId) {
      this._lastWorkspaceStorage.save(workspace.id, userId)
    }
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  private _syncWorkspaceSelection(member: Member) {
    const userId = member.user.publicId
    if (!userId) {
      return
    }

    // Use global workspace selection if available in dashboard workspaces
    const globalWorkspace =
      this.workspaceSelectorVm.workspacesWhereUserCanViewDashboard.find(
        (ws) => ws.id === member.workspace.id,
      )
    if (globalWorkspace) {
      this.selectWorkspaceForFiltering(globalWorkspace)
      return
    }

    // Fall back to saved workspace
    const savedWorkspaceId = this._lastWorkspaceStorage.load(userId)
    if (savedWorkspaceId) {
      const savedWorkspace =
        this.workspaceSelectorVm.workspacesWhereUserCanViewDashboard.find(
          (ws) => ws.id === savedWorkspaceId,
        )
      if (savedWorkspace) {
        this.selectWorkspaceForFiltering(savedWorkspace)
        return
      }
    }

    // Fall back to first available workspace
    if (this.workspaceSelectorVm.workspacesWhereUserCanViewDashboard.length) {
      this.selectWorkspaceForFiltering(
        this.workspaceSelectorVm.workspacesWhereUserCanViewDashboard[0],
      )
    }
  }
}
